import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Award, Loader2, Save, Upload, Trash2, FileText, ChevronDown, ChevronRight,
  UserCheck, ClipboardList, ShieldCheck, AlertCircle,
} from "lucide-react";
import { adminApi } from "../api";
import { toast } from "sonner";
import CursoTabsNav from "../components/CursoTabsNav";

const TIPO_LABELS = {
  PRACTITIONER_PNL: "Practitioner de PNL",
  MASTER_PNL: "Master de PNL",
  COACHING: "Certificação de Coaching",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

/** Botão de upload compacto que aceita PDF por defeito. */
function UploadButton({ onFile, testId, disabled, label = "Upload", accept = ".pdf,.docx,.zip" }) {
  const inputId = `file-${testId}`;
  return (
    <label
      htmlFor={inputId}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border cursor-pointer transition ${
        disabled
          ? "bg-[#F5F2EB] border-[#E6E1D6] text-[#B8B0A0] cursor-not-allowed"
          : "bg-white border-[#A8843C] text-[#A8843C] hover:bg-[#A8843C] hover:text-white"
      }`}
    >
      <Upload size={12} />
      {label}
      <input
        id={inputId}
        type="file"
        accept={accept}
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
    </label>
  );
}

/** Painel expansível por formando: exibe docs + testes com upload. */
function InscricaoPanel({ cursoId, ficha, enunciados, onChange }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [testes, setTestes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({});

  const fid = ficha.ID_FichaInscricao;

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        adminApi.cursoDocumentosStatus(cursoId, fid),
        adminApi.cursoTestesList(cursoId, fid),
      ]);
      setStatus(s);
      setTestes(t.testes || []);
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setLoading(false); }
  }, [cursoId, fid, open]);

  useEffect(() => { load(); }, [load]);

  const uploadDoc = async (tipo, file) => {
    setUploading((u) => ({ ...u, [tipo]: true }));
    try {
      await adminApi.cursoDocumentoUpload(cursoId, fid, tipo, file);
      toast.success(`Documento ${tipo} carregado.`);
      await load();
      onChange?.();
    } catch (e) { toast.error(e.message || "Erro no upload"); }
    finally { setUploading((u) => ({ ...u, [tipo]: false })); }
  };

  const deleteDoc = async (tipo) => {
    if (!confirm(`Remover ${tipo}?`)) return;
    try {
      await adminApi.cursoDocumentoDelete(cursoId, fid, tipo);
      toast.success("Removido.");
      await load();
      onChange?.();
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  const uploadTeste = async (idEnunciado, file) => {
    setUploading((u) => ({ ...u, [`teste-${idEnunciado}`]: true }));
    try {
      await adminApi.cursoTesteUpload(cursoId, fid, file, { idEnunciado });
      toast.success("Teste carregado.");
      await load();
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setUploading((u) => ({ ...u, [`teste-${idEnunciado}`]: false })); }
  };

  const docs = status?.docs || [];
  const done = docs.filter((d) => d.uploaded).length;
  const total = docs.length;
  const testesDone = testes.length;
  const testesTotal = enunciados.length;

  return (
    <div
      data-testid={`inscricao-panel-${fid}`}
      className="border border-[#E6E1D6] rounded-lg bg-white"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`inscricao-toggle-${fid}`}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#FAF8F2] transition"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <UserCheck size={14} className="text-[#A8843C]" />
        <span className="font-medium text-sm text-[#1B1B1B]">{ficha.NomeCompleto}</span>
        <span className="text-xs text-[#978D78]">#{fid}</span>
        <div className="ml-auto flex items-center gap-3 text-xs">
          {total > 0 && (
            <span
              data-testid={`inscricao-docs-badge-${fid}`}
              className={`px-2 py-0.5 rounded ${
                done === total ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
              }`}
            >
              Docs {done}/{total}
            </span>
          )}
          {testesTotal > 0 && (
            <span
              data-testid={`inscricao-testes-badge-${fid}`}
              className={`px-2 py-0.5 rounded ${
                testesDone === testesTotal ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
              }`}
            >
              Testes {testesDone}/{testesTotal}
            </span>
          )}
        </div>
      </button>

      {open && (
        <div className="border-t border-[#F0EBDF] p-3 space-y-4">
          {loading && (
            <div className="text-sm text-[#978D78] flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> A carregar…
            </div>
          )}

          {/* Documentos do formando */}
          {docs.length > 0 && (
            <div>
              <h4 className="text-[11px] uppercase tracking-widest text-[#978D78] font-semibold mb-2">
                Documentos da certificação
              </h4>
              <div className="space-y-1.5">
                {docs.map((d) => {
                  const isUp = !!uploading[d.tipo];
                  return (
                    <div
                      key={d.tipo}
                      data-testid={`doc-row-${fid}-${d.tipo}`}
                      className="flex items-center gap-2 px-2.5 py-1.5 bg-[#FAF8F2] rounded text-sm"
                    >
                      <FileText size={13} className={d.uploaded ? "text-green-700" : "text-[#B8B0A0]"} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#1B1B1B]">{d.label}</div>
                        {d.hint && !d.uploaded && (
                          <div className="text-[11px] text-[#978D78] leading-tight">{d.hint}</div>
                        )}
                        {d.uploaded && (
                          <div className="text-[11px] text-[#978D78]">
                            {fmtDate(d.data_upload)} · {d.uploaded_por}
                          </div>
                        )}
                      </div>
                      {d.uploaded && d.url && (
                        <a
                          href={d.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`doc-download-${fid}-${d.tipo}`}
                          className="text-xs text-[#A8843C] underline"
                        >
                          Ver
                        </a>
                      )}
                      <UploadButton
                        testId={`doc-upload-${fid}-${d.tipo}`}
                        onFile={(f) => uploadDoc(d.tipo, f)}
                        disabled={isUp}
                        label={d.uploaded ? "Substituir" : "Upload"}
                      />
                      {d.uploaded && (
                        <button
                          type="button"
                          onClick={() => deleteDoc(d.tipo)}
                          data-testid={`doc-delete-${fid}-${d.tipo}`}
                          className="text-[#B8B0A0] hover:text-red-600"
                          title="Remover"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Testes preenchidos */}
          {enunciados.length > 0 && (
            <div>
              <h4 className="text-[11px] uppercase tracking-widest text-[#978D78] font-semibold mb-2">
                Testes de avaliação
              </h4>
              <div className="space-y-1.5">
                {enunciados.map((en) => {
                  const teste = testes.find((t) => t.ID_Enunciado === en.ID_Enunciado);
                  const isUp = !!uploading[`teste-${en.ID_Enunciado}`];
                  return (
                    <div
                      key={en.ID_Enunciado}
                      data-testid={`teste-row-${fid}-${en.ID_Enunciado}`}
                      className="flex items-center gap-2 px-2.5 py-1.5 bg-[#FAF8F2] rounded text-sm"
                    >
                      <ClipboardList size={13} className={teste ? "text-green-700" : "text-[#B8B0A0]"} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#1B1B1B]">{en.Nome}</div>
                        {teste && (
                          <div className="text-[11px] text-[#978D78]">
                            Preenchido a {fmtDate(teste.DataUpload)}
                            {teste.Nota != null && ` · Nota ${teste.Nota}`}
                          </div>
                        )}
                      </div>
                      {teste?.url && (
                        <a
                          href={teste.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`teste-download-${fid}-${en.ID_Enunciado}`}
                          className="text-xs text-[#A8843C] underline"
                        >
                          Ver
                        </a>
                      )}
                      <UploadButton
                        testId={`teste-upload-${fid}-${en.ID_Enunciado}`}
                        onFile={(f) => uploadTeste(en.ID_Enunciado, f)}
                        disabled={isUp}
                        label={teste ? "Substituir" : "Upload teste"}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {docs.length === 0 && enunciados.length === 0 && (
            <div className="text-sm text-[#978D78]">
              Este curso não tem certificação nem avaliação configuradas.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function AdminCursoCertificacao() {
  const [searchParams] = useSearchParams();
  const cursoId = searchParams.get("curso") || "";

  const [config, setConfig] = useState(null);
  const [enunciados, setEnunciados] = useState([]);
  const [inscricoes, setInscricoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingEn, setUploadingEn] = useState(false);
  const [enNome, setEnNome] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  const load = useCallback(async () => {
    if (!cursoId) return;
    setLoading(true);
    try {
      const [cfg, en, ins] = await Promise.all([
        adminApi.cursoCertificacaoGet(cursoId),
        adminApi.cursoEnunciadosList(cursoId),
        adminApi.cursoInscricoesList(cursoId),
      ]);
      setConfig(cfg);
      setEnunciados(en.enunciados || []);
      setInscricoes(ins.data || ins.fichas || ins.inscricoes || []);
    } catch (e) { toast.error(e.message || "Erro a carregar"); }
    finally { setLoading(false); }
  }, [cursoId]);

  useEffect(() => { load(); }, [load, refreshTick]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await adminApi.cursoCertificacaoSet(cursoId, {
        TipoCertificacao: config.TipoCertificacao || null,
        TemAvaliacao: !!config.TemAvaliacao,
      });
      setConfig(r);
      toast.success("Configuração guardada.");
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setSaving(false); }
  };

  const uploadEnunciado = async (file) => {
    const nome = enNome.trim() || file.name.replace(/\.[^.]+$/, "");
    setUploadingEn(true);
    try {
      await adminApi.cursoEnunciadoUpload(cursoId, file, nome);
      toast.success("Enunciado carregado.");
      setEnNome("");
      setRefreshTick((t) => t + 1);
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setUploadingEn(false); }
  };

  const deleteEnunciado = async (id, nome) => {
    if (!confirm(`Remover enunciado "${nome}"?`)) return;
    try {
      await adminApi.cursoEnunciadoDelete(cursoId, id);
      toast.success("Removido.");
      setRefreshTick((t) => t + 1);
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  if (!cursoId) {
    return (
      <div className="max-w-5xl">
        <div className="rounded-lg border border-[#E6E1D6] bg-white p-6 text-sm text-[#978D78]">
          Seleccione um curso primeiro em <a href="/admin/cursos/edit" className="underline text-[#A8843C]">Cursos → Geral</a>.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl" data-testid="admin-curso-certificacao">
      <div className="mb-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#A8843C] grid place-items-center text-white shrink-0">
          <Award size={20} />
        </div>
        <div>
          <h1 className="text-xl text-[#1B1B1B]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}>
            Certificação & Avaliação
          </h1>
          <p className="text-sm text-[#6E6754]">
            License Agreement, Diploma Practitioner, docs Coaching e enunciados/testes de avaliação.
          </p>
        </div>
      </div>

      <CursoTabsNav cursoId={cursoId} current="certificacao" />

      {loading || !config ? (
        <div className="p-6 text-sm text-[#978D78] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> A carregar…
        </div>
      ) : (
        <div className="space-y-6">
          {/* Configuração */}
          <section
            data-testid="cert-config-section"
            className="bg-white border border-[#E6E1D6] rounded-lg p-5"
          >
            <h2 className="text-sm font-semibold text-[#1B1B1B] mb-3 flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#A8843C]" /> Configuração do curso
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-[#978D78] font-semibold">
                  Tipo de Certificação
                </label>
                <select
                  data-testid="cert-tipo-select"
                  value={config.TipoCertificacao || ""}
                  onChange={(e) => setConfig({ ...config, TipoCertificacao: e.target.value || null })}
                  className="mt-1 w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm bg-white"
                >
                  <option value="">— Sem certificação especial —</option>
                  {(config.tipos_disponiveis || []).map((t) => (
                    <option key={t} value={t}>{TIPO_LABELS[t] || t}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="cert-avaliacao-toggle"
                  checked={!!config.TemAvaliacao}
                  onChange={(e) => setConfig({ ...config, TemAvaliacao: e.target.checked })}
                />
                Tem avaliação (permite uploads de enunciados/testes)
              </label>
              <div className="text-right">
                <button
                  type="button"
                  onClick={saveConfig}
                  disabled={saving}
                  data-testid="cert-save-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#A8843C] text-white text-sm rounded hover:bg-[#8B6E32] disabled:opacity-60"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Guardar
                </button>
              </div>
            </div>
            {config.TipoCertificacao && (
              <div className="mt-3 text-xs text-[#6E6754] bg-[#FAF8F2] px-3 py-2 rounded">
                <AlertCircle size={12} className="inline mr-1" />
                Formandos deste curso terão de submeter os documentos exigidos pelo tipo
                <b> {TIPO_LABELS[config.TipoCertificacao]}</b>.
              </div>
            )}
          </section>

          {/* Enunciados */}
          {config.TemAvaliacao && (
            <section
              data-testid="cert-enunciados-section"
              className="bg-white border border-[#E6E1D6] rounded-lg p-5"
            >
              <h2 className="text-sm font-semibold text-[#1B1B1B] mb-3 flex items-center gap-2">
                <ClipboardList size={16} className="text-[#A8843C]" /> Enunciados de avaliação
                <span className="text-xs font-normal text-[#978D78]">
                  ({enunciados.length} carregado{enunciados.length !== 1 ? "s" : ""})
                </span>
              </h2>

              <div className="space-y-1.5 mb-4">
                {enunciados.length === 0 && (
                  <div className="text-sm text-[#978D78] italic">
                    Ainda não carregou enunciados. Sugerido: 1 para PNL, 2 para Coaching.
                  </div>
                )}
                {enunciados.map((en) => (
                  <div
                    key={en.ID_Enunciado}
                    data-testid={`enunciado-row-${en.ID_Enunciado}`}
                    className="flex items-center gap-2 px-3 py-2 bg-[#FAF8F2] rounded text-sm"
                  >
                    <FileText size={13} className="text-[#A8843C]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#1B1B1B]">{en.Nome}</div>
                      <div className="text-[11px] text-[#978D78]">
                        {fmtDate(en.DataUpload)} · {en.UploadedPor}
                      </div>
                    </div>
                    <a
                      href={en.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`enunciado-download-${en.ID_Enunciado}`}
                      className="text-xs text-[#A8843C] underline"
                    >
                      Ver
                    </a>
                    <button
                      type="button"
                      onClick={() => deleteEnunciado(en.ID_Enunciado, en.Nome)}
                      data-testid={`enunciado-delete-${en.ID_Enunciado}`}
                      className="text-[#B8B0A0] hover:text-red-600"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 border-t border-[#F0EBDF] pt-3">
                <input
                  type="text"
                  value={enNome}
                  onChange={(e) => setEnNome(e.target.value)}
                  placeholder="Nome do enunciado (ex: Teste PNL Nível 1)"
                  data-testid="enunciado-nome-input"
                  className="flex-1 px-3 py-2 border border-[#E6E1D6] rounded text-sm"
                />
                <UploadButton
                  testId="enunciado-upload-btn"
                  onFile={uploadEnunciado}
                  disabled={uploadingEn}
                  label={uploadingEn ? "A enviar…" : "+ Enunciado"}
                  accept=".pdf,.docx"
                />
              </div>
            </section>
          )}

          {/* Inscrições (docs formando + testes) */}
          {(config.TipoCertificacao || config.TemAvaliacao) && (
            <section
              data-testid="cert-inscricoes-section"
              className="bg-white border border-[#E6E1D6] rounded-lg p-5"
            >
              <h2 className="text-sm font-semibold text-[#1B1B1B] mb-3 flex items-center gap-2">
                <UserCheck size={16} className="text-[#A8843C]" /> Formandos ({inscricoes.length})
              </h2>
              {inscricoes.length === 0 ? (
                <div className="text-sm text-[#978D78] italic">Sem inscrições activas.</div>
              ) : (
                <div className="space-y-2">
                  {inscricoes.map((f) => (
                    <InscricaoPanel
                      key={f.ID_FichaInscricao}
                      cursoId={cursoId}
                      ficha={f}
                      enunciados={config.TemAvaliacao ? enunciados : []}
                      onChange={() => setRefreshTick((t) => t + 1)}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
